/**
 * AdminHouseRisk — /admin/house
 *
 * The house's global risk control surface:
 *  - Capacity gauge: total open liability vs the safe bankroll ceiling.
 *  - Editable house limits (bankroll, liability %, stake/payout/exposure caps,
 *    daily stake cap, live-betting toggle). Values shown in Rands.
 *
 * Mirrors the existing admin page styling (white cards, violet accents).
 */
import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getHouseConfig, updateHouseConfig, type HouseConfig } from '../../services/api';
import { formatZAR } from '../../utils/currency';

interface AdminCtx {
  adminEmail: string | null;
}

// ── cents ↔ rands helpers (form works in Rands; API speaks cents) ────────────

function centsToRands(cents: number): string {
  return (cents / 100).toString();
}

/** Parse a Rands string to integer cents; returns null when not a valid number. */
function randsToCents(rands: string): number | null {
  const trimmed = rands.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

// The editable cents-valued fields, with field key + label + helper text.
const RAND_FIELDS: {
  key: keyof HouseConfig;
  label: string;
  helper: string;
}[] = [
  {
    key: 'house_bankroll_cents',
    label: 'House bankroll',
    helper: 'Total funds the house can safely cover payouts from.',
  },
  {
    key: 'max_stake_cents',
    label: 'Max stake (single bet)',
    helper: 'Largest stake allowed on a single bet leg or bet.',
  },
  {
    key: 'max_payout_cents',
    label: 'Max payout (single bet)',
    helper: 'Cap on the potential payout of a single (non-accumulator) bet.',
  },
  {
    key: 'max_acca_payout_cents',
    label: 'Max payout (accumulator)',
    helper: 'Cap on the potential payout of a multi-leg accumulator.',
  },
  {
    key: 'max_market_exposure_cents',
    label: 'Max exposure per market',
    helper: 'Auto-suspend a market once open liability on it passes this.',
  },
  {
    key: 'max_user_daily_stake_cents',
    label: 'Max daily stake per user',
    helper: 'Total a single user may stake across all bets in one day.',
  },
];

// ── Capacity gauge ────────────────────────────────────────────────────────

function CapacityGauge({ config }: { config: HouseConfig }) {
  const bankroll = config.house_bankroll_cents;
  const ceilingCents = Math.floor((bankroll * config.max_total_liability_pct) / 100);
  const pct = config.capacity_used_pct;

  const barColor =
    pct > 85 ? 'bg-red-500' : pct >= 60 ? 'bg-amber-400' : 'bg-green-500';
  const labelColor =
    pct > 85 ? 'text-red-600' : pct >= 60 ? 'text-amber-600' : 'text-green-600';

  return (
    <div className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      <h2 className="mb-1 text-lg font-semibold text-gray-800">House capacity</h2>
      <p className="mb-4 text-sm text-gray-500">
        This is how much the house could pay out on open bets right now vs the safe ceiling.
      </p>

      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-sm font-medium text-gray-700">
          House liability: {formatZAR(config.total_open_liability_cents)} of{' '}
          {formatZAR(ceilingCents)} capacity
        </span>
        <span className={`text-xl font-bold ${labelColor}`}>{pct}%</span>
      </div>

      <div className="h-4 w-full overflow-hidden rounded-full bg-gray-200">
        <div
          className={`h-full rounded-full transition-all ${barColor}`}
          style={{ width: `${Math.min(Math.max(pct, 0), 100)}%` }}
        />
      </div>

      <p className="mt-3 text-xs text-gray-400">
        Safe ceiling = bankroll ({formatZAR(bankroll)}) × max total liability (
        {config.max_total_liability_pct}%). New bets are declined once liability passes the
        ceiling.
      </p>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

export default function AdminHouseRisk() {
  const { adminEmail } = useOutletContext<AdminCtx>();
  const [config, setConfig] = useState<HouseConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Form state: Rand strings for cents fields, plus % and the live toggle.
  const [randForm, setRandForm] = useState<Record<string, string>>({});
  const [liabilityPct, setLiabilityPct] = useState<string>('');
  const [liveBetting, setLiveBetting] = useState<boolean>(false);
  // Dynamic odds + live bet-delay knobs (integers; %/seconds).
  const [dynamicOdds, setDynamicOdds] = useState<boolean>(false);
  const [sensitivity, setSensitivity] = useState<string>('');
  const [maxDrift, setMaxDrift] = useState<string>('');
  const [delaySeconds, setDelaySeconds] = useState<string>('');
  const [tolerance, setTolerance] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = async (email: string) => {
    try {
      const res = await getHouseConfig(email);
      hydrate(res.data);
      setError(null);
    } catch {
      setError('Failed to load house risk config.');
    }
  };

  const hydrate = (c: HouseConfig) => {
    setConfig(c);
    const form: Record<string, string> = {};
    for (const f of RAND_FIELDS) {
      form[f.key] = centsToRands(c[f.key] as number);
    }
    setRandForm(form);
    setLiabilityPct(String(c.max_total_liability_pct));
    setLiveBetting(c.live_betting_enabled);
    setDynamicOdds(c.dynamic_odds_enabled);
    setSensitivity(String(c.dynamic_odds_sensitivity_pct));
    setMaxDrift(String(c.dynamic_odds_max_drift_pct));
    setDelaySeconds(String(c.live_bet_delay_seconds));
    setTolerance(String(c.live_bet_odds_tolerance_pct));
  };

  useEffect(() => {
    if (adminEmail) load(adminEmail);
  }, [adminEmail]);

  // Validate every Rand field + the % field before allowing save.
  const validation = useMemo(() => {
    for (const f of RAND_FIELDS) {
      if (randsToCents(randForm[f.key] ?? '') === null) {
        return `${f.label} must be a valid amount (0 or more).`;
      }
    }
    const pct = Number(liabilityPct);
    if (!Number.isInteger(pct) || pct < 1 || pct > 100) {
      return 'Max total liability % must be a whole number between 1 and 100.';
    }
    const intIn = (v: string, lo: number, hi: number) => {
      const n = Number(v);
      return Number.isInteger(n) && n >= lo && n <= hi;
    };
    if (!intIn(sensitivity, 0, 100)) return 'Odds sensitivity must be a whole number 0–100.';
    if (!intIn(maxDrift, 0, 50)) return 'Max odds drift must be a whole number 0–50.';
    if (!intIn(delaySeconds, 0, 30)) return 'Live bet delay must be a whole number 0–30 seconds.';
    if (!intIn(tolerance, 0, 50)) return 'Live odds tolerance must be a whole number 0–50.';
    return null;
  }, [randForm, liabilityPct, sensitivity, maxDrift, delaySeconds, tolerance]);

  const handleSave = async () => {
    if (!adminEmail || validation) return;
    setSaving(true);
    setToast(null);
    try {
      const body: Record<string, number | boolean> = {
        max_total_liability_pct: Number(liabilityPct),
        live_betting_enabled: liveBetting,
        dynamic_odds_enabled: dynamicOdds,
        dynamic_odds_sensitivity_pct: Number(sensitivity),
        dynamic_odds_max_drift_pct: Number(maxDrift),
        live_bet_delay_seconds: Number(delaySeconds),
        live_bet_odds_tolerance_pct: Number(tolerance),
      };
      for (const f of RAND_FIELDS) {
        const cents = randsToCents(randForm[f.key]);
        if (cents !== null) body[f.key] = cents;
      }
      const res = await updateHouseConfig(adminEmail, body);
      hydrate(res.data);
      setToast('House limits saved.');
      setTimeout(() => setToast(null), 3000);
    } catch {
      setError('Failed to save house risk config.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold text-gray-900">House Risk</h1>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
      )}

      {!config && !error && <p className="text-sm text-gray-400">Loading…</p>}

      {config && (
        <>
          <CapacityGauge config={config} />

          <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="mb-1 text-lg font-semibold text-gray-800">Limits</h2>
            <p className="mb-5 text-sm text-gray-500">
              All amounts are in Rands. Saving updates the live limits immediately — no
              redeploy needed.
            </p>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {/* Max total liability % */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Max total liability (%)
                </label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={liabilityPct}
                  onChange={(e) => setLiabilityPct(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
                />
                <p className="mt-1 text-xs text-gray-400">
                  Reject new bets once open liability passes this % of bankroll.
                </p>
              </div>

              {/* Rand-valued fields */}
              {RAND_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="block text-sm font-medium text-gray-700">
                    {f.label} (R)
                  </label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={randForm[f.key] ?? ''}
                    onChange={(e) =>
                      setRandForm((prev) => ({ ...prev, [f.key]: e.target.value }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
                  />
                  <p className="mt-1 text-xs text-gray-400">{f.helper}</p>
                </div>
              ))}
            </div>

            {/* Live betting toggle */}
            <div className="mt-5 flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <input
                id="live-betting"
                type="checkbox"
                checked={liveBetting}
                onChange={(e) => setLiveBetting(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
              />
              <label htmlFor="live-betting" className="text-sm">
                <span className="font-medium text-gray-700">Live (in-play) betting enabled</span>
                <span className="mt-0.5 block text-xs text-gray-400">
                  When off, bets on events already in play are rejected.
                </span>
              </label>
            </div>

            {/* ── Odds engine & live bet-delay ─────────────────────────── */}
            <div className="mt-6 rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-800">Odds engine & live betting</h3>
              <p className="mt-0.5 mb-4 text-xs text-gray-400">
                The dynamic engine nudges prices toward where the money goes (shortens
                heavily-backed selections, lengthens the rest) to balance the book. Live
                bets are held briefly and re-checked so no one is filled at a stale price.
              </p>

              <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <input
                  id="dynamic-odds"
                  type="checkbox"
                  checked={dynamicOdds}
                  onChange={(e) => setDynamicOdds(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                />
                <label htmlFor="dynamic-odds" className="text-sm">
                  <span className="font-medium text-gray-700">Dynamic (volume-based) odds enabled</span>
                  <span className="mt-0.5 block text-xs text-gray-400">
                    When off, odds follow the feed consensus only (no movement on volume).
                  </span>
                </label>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Odds sensitivity (%)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={sensitivity}
                    onChange={(e) => setSensitivity(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    How strongly the money pulls the price (0 = none, 100 = fully). 10 is conservative.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Max odds drift per update (%)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={50}
                    value={maxDrift}
                    onChange={(e) => setMaxDrift(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    Clamp on how far a single recompute may move a price (anti-whipsaw).
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Live bet delay (seconds)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={30}
                    value={delaySeconds}
                    onChange={(e) => setDelaySeconds(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    In-play bets are held this long before acceptance (0 = no delay).
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Live odds tolerance (%)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={50}
                    value={tolerance}
                    onChange={(e) => setTolerance(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    If a live price moves more than this during the delay, the bet is rejected to reconfirm.
                  </p>
                </div>
              </div>
            </div>

            {validation && (
              <p className="mt-4 text-sm text-red-600">{validation}</p>
            )}

            <div className="mt-5 flex items-center gap-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !!validation}
                className="rounded-lg bg-violet-600 px-5 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Save limits'}
              </button>
              {toast && <span className="text-sm font-medium text-green-600">{toast}</span>}
              {config.updated_by && (
                <span className="ml-auto text-xs text-gray-400">
                  Last updated by {config.updated_by}
                </span>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
